import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { GetOrganization200ServiceStatus } from "~/api/model";
import { useGetOrganization } from "~/api/organizations/organizations";
import { useGetCurrentUser } from "~/api/users/users";
import { DEFAULT_FREE_TRIAL_LIMIT } from "~/features/billing/constants";
import { UsageLimitModal } from "~/features/billing/UsageLimitModal";
import { useMedications } from "~/features/chat/hooks/useMedications";
import { getLoginUrl } from "~/helpers";
import { useFtue } from "~/hooks/useFtue";
import { useProactiveTokenRefresh } from "~/hooks/useProactiveTokenRefresh";
import { AppHeader } from "../AppHeader";
import { AppLogo } from "../AppLogo";
import ErrorBoundaryWrapper from "../ErrorBoundaryWrapper";
import { PaymentFailedModal } from "../PaymentFailedModal";

export const AppLayout = () => {
  const location = useLocation();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: userInfo, isLoading } = useGetCurrentUser();
  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );
  const [isUsageLimitDismissed, setIsUsageLimitDismissed] = useState(false);

  useEffect(() => {
    setIsUsageLimitDismissed(false);
  }, [organizationId]);

  useProactiveTokenRefresh();
  useMedications();

  const { shouldShowFtue } = useFtue();
  const isChatPage = location.pathname.includes("/chat");

  const isPaymentFailed =
    organizationInfo?.serviceStatus ===
    GetOrganization200ServiceStatus.PAYMENT_FAILED;

  const freeTrialLimit =
    Number(organizationInfo?.freeTrialLimit) || DEFAULT_FREE_TRIAL_LIMIT;
  const isPaymentRequired =
    organizationInfo?.serviceStatus ===
    GetOrganization200ServiceStatus.PAYMENT_REQUIRED;
  const isLimitReached =
    (organizationInfo?.pagesUsedInThisBillingPeriod ?? 0) >= freeTrialLimit;

  const shouldShowUsageLimitModal =
    (isLimitReached &&
      organizationInfo?.serviceStatus ===
        GetOrganization200ServiceStatus.FREE_TRIAL) ||
    isPaymentRequired;

  if (isLoading) {
    return null;
  }

  if (!userInfo) {
    return <Navigate to={getLoginUrl()} replace />;
  }

  return (
    <ErrorBoundaryWrapper>
      <div
        className={
          isChatPage ? "h-screen-safe overflow-hidden" : "min-h-screen-safe"
        }
      >
        {!isChatPage &&
          (shouldShowFtue ? (
            <div className="w-full border-b max-w-[1920px] mx-auto">
              <div className="mx-auto p-3 px-4 sm:px-6 flex h-[64px] items-center justify-center">
                <AppLogo />
              </div>
            </div>
          ) : (
            <AppHeader />
          ))}
        <main className={isChatPage ? "h-full" : ""}>
          <ErrorBoundaryWrapper
            fullScreen={false}
            resetKeys={[location.pathname]}
          >
            <Outlet />
          </ErrorBoundaryWrapper>
        </main>

        {/* Modal for payment failed */}
        {isPaymentFailed && organizationId && (
          <PaymentFailedModal
            open={isPaymentFailed}
            organizationId={organizationId}
          />
        )}

        {/* Modal for usage limit reached (hard gate).
            Dismissal lives in component state, so it re-appears on refresh. */}
        {shouldShowUsageLimitModal &&
          organizationId &&
          !isUsageLimitDismissed && (
            <UsageLimitModal
              organizationId={organizationId}
              freeTrialLimit={freeTrialLimit}
              isLimitReached={isLimitReached}
              onClose={() => setIsUsageLimitDismissed(true)}
              isHardMode={isPaymentRequired}
            />
          )}
      </div>
    </ErrorBoundaryWrapper>
  );
};
